import { JSX, useEffect, useRef, useState } from 'react';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Image from 'next/image';
import Icons from 'components/atoms/Icons';
import {
  formatLocationUrl,
  formatSearchResultUrl,
} from 'assets/utils/formatUrl';
import Axios from 'axios';
import { getLocationsSearchSrc, getSearchWidget } from 'lib/util';
import { LocationType } from 'components/types/LocationTypes';
import { LocationFields as DynamicLocationFields } from 'components/types/MapTypes';
import { ItemValue } from 'components/types/ListItem';
import Modal from 'assets/shared/Modal';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import { StyleguideComponentProps } from 'lib/component-props';
import { LocationItem } from 'components/types/LocationTypes';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';

const AZURE_MAPS_ENDPOINT = 'https://atlas.microsoft.com';

const getAzureMapsKey = () =>
  process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

type LatLng = {
  lat: number;
  lng: number;
};

type AzureSuggestion = {
  id: string;
  label: string;
  address: {
    label?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

const getDistance = (
  originLat: number,
  originLon: number,
  latitude: number,
  longitude: number
) => {
  const EarthMeanRadiusInKilometers = 6371.0087714;
  const φ1 = (originLat * Math.PI) / 180;
  const φ2 = (latitude * Math.PI) / 180;
  const Δφ = ((latitude - originLat) * Math.PI) / 180;
  const Δλ = ((longitude - originLon) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EarthMeanRadiusInKilometers * c * 0.621371192;
};

type DynamicLocationContentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Title: Field<string>;
    Description: Field<string>;
    Locations: Array<DynamicLocationFields>;
    Keywords: Field<string>;
    LocationTypes: Array<LocationType>;
    ZipCode: Field<string>;
    Radius: ItemValue;
  };
};

let defaultSearchBody: any = {
  content: {},
  limit: 100,
};

type LocationCardProps = {
  location: DynamicLocationFields;
  coords?: LatLng | null;
};

const LocationCard = (props: LocationCardProps) => {
  const { location, coords } = props;

  const distance =
    coords?.lat && coords?.lng
      ? getDistance(
          coords.lat,
          coords.lng,
          parseFloat(location.latitude),
          parseFloat(location.longitude)
        )
      : null;

  return (
    <div
      className="location-item bg-secondary"
      key={`location-${location.title}`}
    >
      <a
        href={formatSearchResultUrl(location?.link)}
        key={location.id}
        className="featured-item bg-secondary"
      >
        <div className="content-container">
          <div
            className="address"
            itemProp="hospitalAffiliation"
            itemType="http://schema.org/Hospital"
          >
            {location?.city && location?.state && (
              <p className="item-title">
                {location.city}, {location.state}
              </p>
            )}

            <div className="address-details">
              <svg className="icon icon-location">
                <Icons id="icon-pdc-location" />
              </svg>

              {location?.street1 && (
                <p
                  className="item-subtitle"
                  itemProp="address"
                  itemScope
                  itemType="http://schema.org/PostalAddress"
                >
                  {location.street1}
                </p>
              )}
            </div>

            <div className="distance">
              <svg className="icon icon-distance">
                <Icons id="icon-distance" />
              </svg>

              {distance && (
                <p
                  className="osf-label"
                  itemProp="address"
                  itemScope
                  itemType="http://schema.org/PostalAddress"
                >
                  {distance.toFixed(2)} miles away
                </p>
              )}
            </div>
          </div>

          <div className="divider"></div>

          <div className="item-name" itemProp="hospitalAffiliation">
            <h3 className="osf-display-h3">{location.title}</h3>
          </div>
        </div>

        <div className="item-image">
          {location?.image ? (
            <Image src={location.image} alt={`image-${location.id}`} fill />
          ) : (
            <Image
              alt={`image-${location.id}`}
              src="https://osf-p-001.sitecorecontenthub.cloud/api/public/content/placeholder-location-transparent"
              fill
            />
          )}
        </div>
      </a>
    </div>
  );
};

type ManualLocationCardProps = {
  location: LocationItem;
};

const ManualLocationCard = (props: ManualLocationCardProps) => {
  const { location } = props;
  const { fields } = location;

  return (
    <div
      className="location-item bg-secondary"
      key={`location-${location.fields.LocationName}`}
    >
      <a
        href={formatLocationUrl(fields.LocationId.value, location.name)}
        key={location.id}
        className="featured-item bg-secondary"
      >
        <div className="content-container">
          <div
            className="address"
            itemProp="hospitalAffiliation"
            itemType="http://schema.org/Hospital"
          >
            {fields?.City?.value && fields?.State?.value && (
              <p className="item-title">
                <Text field={fields.City} />, <Text field={fields.State} />
              </p>
            )}

            <div className="address-details">
              <svg className="icon icon-location">
                <Icons id="icon-pdc-location" />
              </svg>

              <p
                className="item-subtitle"
                itemProp="address"
                itemScope
                itemType="http://schema.org/PostalAddress"
              >
                {fields?.Street1 && <Text field={fields.Street1} />}
              </p>
            </div>

            {location?.distance && (
              <div className="distance">
                <svg className="icon icon-distance">
                  <Icons id="icon-distance" />
                </svg>

                <p
                  className="osf-label"
                  itemProp="address"
                  itemScope
                  itemType="http://schema.org/PostalAddress"
                >
                  {location.distance.toFixed(2)} miles away
                </p>
              </div>
            )}
          </div>

          <div className="divider"></div>

          <div className="item-name" itemProp="hospitalAffiliation">
            <h3 className="osf-display-h3">{fields.LocationName.value}</h3>
          </div>
        </div>

        <div className="item-image">
          {fields?.LocationImage?.value?.src ? (
            <Image
              src={fields.LocationImage.value.src}
              alt={fields.LocationImage.value.alt}
              fill
            />
          ) : (
            <Image
              alt={`image-${location.id}`}
              src="https://osf-p-001.sitecorecontenthub.cloud/api/public/content/placeholder-location-transparent"
              fill
            />
          )}
        </div>
      </a>
    </div>
  );
};

type BreakpointsCountType = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
};

const buildLocationString = (address: AzureSuggestion['address']) => {
  return (
    address.label ||
    `${address.city ? `${address.city}, ` : ''}${
      address.state ? `${address.state} ` : ''
    }${address.zip ?? ''}`.trim()
  );
};

const DynamicLocationContent = (
  props: DynamicLocationContentProps
): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const [isDynamic, setIsDynamic] = useState(false);
  const [locations, setLocations] = useState(
    Array<DynamicLocationFields | LocationItem>
  );
  const [totalLocationsCount, setTotalLocationsCount] = useState(0);
  const [size, setSize] = useState('');
  const [locationsShowingCount, setLocationsShowingCount] = useState(0);
  const [locationsShowing, setLocationsShowing] = useState(
    Array<DynamicLocationFields | LocationItem>
  );
  const [inputLocation, setInputLocation] = useState('');
  const [coords, setCoords] = useState<LatLng | null>();
  const [location, setLocation] = useState('');
  const [geolocating, setGeolocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(true);
  const [suggestions, setSuggestions] = useState<Array<AzureSuggestion>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const suggestionRef = useRef<AzureSuggestion | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const DEFAULT_DISTANCE = 16.09;

  const breakpointResultsCount: BreakpointsCountType = {
    xs: 3,
    sm: 3,
    md: 6,
    lg: 6,
    xl: 6,
  };

  const breakpointRowsCount: BreakpointsCountType = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 2,
    xl: 3,
  };

  const convertToKM = (miles: number) => miles / 0.621371192;

  const convertToMI = (km: number) => km * 0.621371192;

  const setAddressState = (
    address: AzureSuggestion['address'],
    updateInput = true
  ) => {
    const locationString = buildLocationString(address);

    setLocation(locationString);

    if (updateInput) {
      setInputLocation(locationString);
    }
  };

  const fetchAzureSuggestions = async (query: string) => {
    const subscriptionKey = getAzureMapsKey();
    const trimmedQuery = query.trim();

    if (!subscriptionKey || !trimmedQuery || trimmedQuery.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    try {
      setIsLoadingSuggestions(true);

      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/fuzzy/json`,
        {
          params: {
            'api-version': '1.0',
            query: trimmedQuery,
            countrySet: 'US',
            limit: 15,
            typeahead: true,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      const normalizedSuggestions = results
        .map((result: any) => {
          const address = result.address;

          const city = address?.municipality;
          const state = address?.countrySubdivision;
          const zip = address?.postalCode;

          const label =
            address?.freeformAddress ||
            result.poi?.name ||
            [city, state, zip].filter(Boolean).join(', ');

          return {
            id: `${label}-${result.type ?? ''}`,
            label,
            address: {
              label,
              city,
              state,
              zip,
            },
          };
        })
        .filter((suggestion: AzureSuggestion) => suggestion.label);

      const uniqueSuggestions = normalizedSuggestions.filter(
        (suggestion: AzureSuggestion, index: number, self: AzureSuggestion[]) =>
          index === self.findIndex(item => item.label === suggestion.label)
      );

      const illinoisSuggestions = uniqueSuggestions.filter(
        (suggestion: AzureSuggestion) =>
          suggestion.address.state?.toLowerCase() === 'il' ||
          suggestion.address.state?.toLowerCase() === 'illinois'
      );

      const nonIllinoisSuggestions = uniqueSuggestions.filter(
        (suggestion: AzureSuggestion) =>
          suggestion.address.state?.toLowerCase() !== 'il' &&
          suggestion.address.state?.toLowerCase() !== 'illinois'
      );

      const prioritizedSuggestions = [
        ...illinoisSuggestions,
        ...nonIllinoisSuggestions,
      ];

      setSuggestions(prioritizedSuggestions.slice(0, 5));
      suggestionRef.current = prioritizedSuggestions[0] || null;
    } catch (error: any) {
      console.error('Azure fuzzy search error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setSuggestions([]);
      suggestionRef.current = null;
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const geocodeSelectedSuggestion = async (suggestion: AzureSuggestion) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) return;

    try {
      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/address/json`,
        {
          params: {
            'api-version': '1.0',
            query: suggestion.label,
            countrySet: 'US',
            limit: 1,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const result = response.data?.results?.[0];

      if (!result?.position?.lat || !result?.position?.lon) return;

      const latLng: LatLng = {
        lat: result.position.lat,
        lng: result.position.lon,
      };

      const address = {
        label: suggestion.label,
        city: suggestion.address.city || result?.address?.municipality,
        state: suggestion.address.state || result?.address?.countrySubdivision,
        zip: suggestion.address.zip || result?.address?.postalCode,
      };

      setLocations([]);
      setCoords(latLng);
      setAddressState(address);
      setSuggestions([]);
    } catch {
      setSuggestions([]);
    }
  };

  const reverseGeocode = async (latLng: LatLng, userLoc?: boolean) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      setGeolocating(false);
      return;
    }

    try {
      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/reverseGeocode`,
        {
          params: {
            'api-version': '2026-01-01',
            coordinates: `${latLng.lng},${latLng.lat}`,
            'subscription-key': subscriptionKey,
            language: 'en-US',
          },
        }
      );

      const azureAddress = response.data?.features?.[0]?.properties?.address;

      const address = {
        city: azureAddress?.locality || azureAddress?.municipality,
        state:
          azureAddress?.adminDistricts?.[0]?.shortName ||
          azureAddress?.adminDistricts?.[0]?.name,
        zip: azureAddress?.postalCode,
      };

      setGeolocating(false);
      setAddressState(address, !userLoc);
    } catch {
      setGeolocating(false);
    }
  };

  const handleChange = (address: string) => {
    setInputLocation(address);
  };

  const handleSelect = async (suggestion?: AzureSuggestion) => {
    const selectedSuggestion = suggestion || suggestionRef.current;

    setLocations([]);

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();

    if (!selectedSuggestion) return;

    await geocodeSelectedSuggestion(selectedSuggestion);
  };

  const geolocate = () => {
    setGeolocating(true);
    setInputLocation('');
    setLocations([]);

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          position &&
          position.coords &&
          position.coords.latitude &&
          position.coords.longitude
        ) {
          const latLng: LatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setCoords(latLng);
          reverseGeocode(latLng, true);
        }
      },
      () => {
        setNoResults(true);
        setCoords(null);
        setLocation('');
        setInputLocation('');
        setGeolocating(false);
        showSelected();
      }
    );
  };

  const search = async () => {
    setSearching(true);

    let geoContext = {};
    const filters = [];

    defaultSearchBody = {
      content: {},
      limit: 100,
    };

    if (coords) {
      geoContext = {
        context: {
          geo: {
            location: {
              lat: coords.lat,
              lon: coords.lng,
            },
          },
        },
      };

      filters.push({
        type: 'geoDistance',
        name: 'coordinate',
        distance: props.fields.Radius?.fields?.heading?.value
          ? `${convertToKM(
              parseInt(props.fields.Radius.fields.heading.value)
            )}km`
          : `${DEFAULT_DISTANCE}km`,
        lat: coords.lat,
        lon: coords.lng,
      });

      defaultSearchBody = {
        ...defaultSearchBody,
        sort: {
          value: [
            {
              name: 'coordinate',
              order: 'asc',
            },
          ],
        },
      };
    }

    if (props.fields.LocationTypes?.length > 0) {
      const specialtiesFilters = props.fields.LocationTypes.map(
        locationType => ({
          type: 'eq',
          name: 'locationtypes',
          value: locationType.name.replaceAll('-', ' '),
        })
      );

      filters.push({ type: 'or', filters: [...specialtiesFilters] });
    }

    if (props.fields.Keywords?.value?.length > 0) {
      defaultSearchBody.query = {
        keyphrase: props.fields.Keywords.value,
      };
    }

    filters.push({
      type: 'eq',
      name: 'publishtolocal',
      value: 'true',
    });

    defaultSearchBody.filter = {
      type: 'and',
      filters,
    };

    try {
      const searchReq = {
        ...geoContext,
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'location',
              sources: [getLocationsSearchSrc()],
              search: defaultSearchBody,
            },
          ],
        },
      };
      const response = await Axios.post('/api/discovery', searchReq);

      const data = response.data;

      if (data.widgets[0].total_item > 0) {
        setNoResults(false);

        const locations = data.widgets[0]
          .content as Array<DynamicLocationFields>;

        let index = 0;
        const numSearches = Math.floor(data.widgets[0]?.total_item / 100) | 0;

        while (index < numSearches) {
          index++;

          defaultSearchBody.offset = 100 * index;

          const searchReq = {
            ...geoContext,
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'location',
                  search: defaultSearchBody,
                },
              ],
            },
          };
          const response = await Axios.post('/api/discovery', searchReq);

          const nextLocations = response.data.widgets[0].content;
          locations.push(...nextLocations);
        }

        setLocations(locations);
        setTotalLocationsCount(data.widgets[0].total_item);

        data.widgets[0].total_item >
        breakpointResultsCount[size as keyof BreakpointsCountType]
          ? setLocationsShowingCount(
              breakpointResultsCount[size as keyof BreakpointsCountType]
            )
          : setLocationsShowingCount(data.widgets[0].total_item);
      } else {
        setNoResults(true);
        showSelected();
      }
    } catch (error) {
      console.log(`error:${error}`);
    } finally {
      setSearching(false);
    }
  };

  const loadMore = () => {
    setLocationsShowingCount(prevCount => {
      const isMobile = size === 'xs' || size === 'sm';

      const itemsToAdd = isMobile
        ? 3
        : breakpointRowsCount[size as keyof BreakpointsCountType];

      if (prevCount + itemsToAdd < totalLocationsCount) {
        return prevCount + itemsToAdd;
      }

      return prevCount + (totalLocationsCount - locationsShowingCount);
    });
  };

  const showSelected = () => {
    setLocations(props.fields.Locations);
    setTotalLocationsCount(props.fields?.Locations?.length);

    props.fields.Locations?.length >
    breakpointResultsCount[size as keyof BreakpointsCountType]
      ? setLocationsShowingCount(
          breakpointResultsCount[size as keyof BreakpointsCountType]
        )
      : setLocationsShowingCount(props.fields.Locations.length);
  };

  const useCurrentLoc = () => {
    geolocate();

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmedInput = inputLocation.trim();

    if (!trimmedInput || trimmedInput.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchAzureSuggestions(trimmedInput);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputLocation]);

  useEffect(() => {
    if ((!isDynamic || noResults) && coords) {
      locations.forEach((location: LocationItem) => {
        const distance =
          coords.lat &&
          coords.lng &&
          getDistance(
            coords.lat,
            coords.lng,
            parseFloat(location.fields.Latitude.value),
            parseFloat(location.fields.Longitude.value)
          );

        location.distance = distance;
      });

      const sortedLocs = locations.sort((a: LocationItem, b: LocationItem) => {
        const firstDist = a.distance ?? Infinity;
        const secondDist = b.distance ?? Infinity;
        return firstDist - secondDist;
      });

      setLocationsShowing(sortedLocs.slice(0, locationsShowingCount));
    } else {
      setLocationsShowing(locations.slice(0, locationsShowingCount));
    }
  }, [locations, locationsShowingCount]);

  useEffect(() => {
    size &&
    totalLocationsCount >
      breakpointResultsCount[size as keyof BreakpointsCountType]
      ? setLocationsShowingCount(
          breakpointResultsCount[size as keyof BreakpointsCountType]
        )
      : setLocationsShowingCount(totalLocationsCount);
  }, [size]);

  useEffect(() => {
    coords && search();
  }, [coords]);

  useEffect(() => {
    const resize = () => {
      const viewportSize = getOsfViewportSize();
      setSize(viewportSize);
    };

    resize();
    window.addEventListener('resize', resize);

    if (
      props.fields?.Keywords?.value ||
      props.fields?.LocationTypes?.length > 0 ||
      props.fields?.ZipCode?.value
    ) {
      setIsDynamic(true);
    } else {
      showSelected();
    }

    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    !props.fields.ZipCode?.value && isDynamic && geolocate();

    if (props.fields.ZipCode?.value) {
      const setZipCoords = async () => {
        const subscriptionKey = getAzureMapsKey();

        if (!subscriptionKey) return;

        try {
          const response = await Axios.get(
            `${AZURE_MAPS_ENDPOINT}/search/address/json`,
            {
              params: {
                'api-version': '1.0',
                query: props.fields.ZipCode.value,
                countrySet: 'US',
                limit: 1,
                'subscription-key': subscriptionKey,
              },
              headers: {
                'Accept-Language': 'en-US',
              },
            }
          );

          const result = response.data?.results?.[0];

          if (!result?.position?.lat || !result?.position?.lon) return;

          const coordinates: LatLng = {
            lat: result.position.lat,
            lng: result.position.lon,
          };

          const address = {
            city: result?.address?.municipality,
            state: result?.address?.countrySubdivision,
            zip: result?.address?.postalCode || props.fields.ZipCode.value,
          };

          setCoords(coordinates);
          setAddressState(address, false);
        } catch (error) {
          console.log(`Azure geolocation error:${error}`);
        }
      };

      setZipCoords();
    }
  }, [isDynamic]);

  return (
    <div
      className={classNames('overhauled dynamic-location-content', marginSize)}
      key={props.rendering.uid}
      id={`dynamic-location-content-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        <div className="header-wrapper">
          {props.fields.Title?.value && (
            <h2 className="heading">{props.fields.Title.value}</h2>
          )}

          {props.fields.Description?.value && (
            <p className="description">{props.fields.Description.value}</p>
          )}

          <div className={noResults ? 'top-row no-results' : 'top-row'}>
            {isDynamic && (
              <div className="current-location">
                <div className="current-location-msg">
                  <div className="icon icon-location">
                    <Icons id="icon-pdc-current-location" />
                  </div>

                  <div className="osf-body-copy">
                    Current Location: {location}{' '}
                    <span className="change-btn">
                      <span className="parenthesis"> (</span>

                      <Modal
                        customClass="modal pdc-modal"
                        openBtnLabel="Change"
                        portalId="pdc-modal-root"
                        shouldCloseOnOverlayClick={true}
                      >
                        <div className="modal-details">
                          <h3>Set Your Location</h3>

                          <p>
                            Providing your location helps us connect you with
                            nearby care that’s convenient for you.
                          </p>

                          <div className="location-filter">
                            <label>Location (City or Zip)</label>

                            <div className="location-distance-panel">
                              <div className="location-distance-panel">
                                <div className="react-autosuggest__container input-icon-container">
                                  <div className="input-wrapper">
                                    <input
                                      placeholder="Enter Location"
                                      className="location-search-input"
                                      disabled={false}
                                      type="text"
                                      value={inputLocation || ''}
                                      onChange={event =>
                                        handleChange(event.target.value)
                                      }
                                      onKeyDown={event => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          handleSelect();
                                        }
                                      }}
                                    />

                                    <button
                                      className="btn-primary no-icon"
                                      onClick={() => handleSelect()}
                                    >
                                      Set
                                    </button>
                                  </div>

                                  {inputLocation?.length > 1 &&
                                    suggestions.length > 0 && (
                                      <div className="react-autosuggest__suggestions-container">
                                        {suggestions.map(suggestion => (
                                          <div
                                            className="typeahead-suggestion"
                                            key={suggestion.id}
                                            onMouseDown={event => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              handleSelect(suggestion);
                                            }}
                                          >
                                            <span className="suggestion-name">
                                              {suggestion.label
                                                .replace(',', '')
                                                .replace(/\d+/g, '')}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                  {inputLocation?.length > 1 &&
                                    !isLoadingSuggestions &&
                                    suggestions.length === 0 && (
                                      <div className="react-autosuggest__suggestions-container">
                                        <div className="typeahead-suggestion">
                                          <span className="suggestion-name">
                                            No locations found
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>

                            <div className="current-location">
                              <div className="icon icon-distance">
                                <Icons id="icon-distance" />
                              </div>

                              <button onClick={() => useCurrentLoc()}>
                                Use my current location
                              </button>
                            </div>
                          </div>
                        </div>
                      </Modal>

                      <span className="parenthesis">)</span>
                    </span>
                  </div>
                </div>

                <div id="pdc-modal-root"></div>

                {noResults && (
                  <p className="no-locations-msg">
                    No locations within{' '}
                    {props.fields.Radius?.fields?.heading?.value ||
                      Math.round(convertToMI(DEFAULT_DISTANCE))}{' '}
                    miles
                  </p>
                )}
              </div>
            )}

            {!noResults && !searching && (
              <p className="showing-msg">
                Showing {locationsShowingCount} of {totalLocationsCount}{' '}
                locations
              </p>
            )}

            {noResults && isDynamic && (
              <div className="showing-all">
                <Icons id="icon-info-thin"></Icons>
                <p>Showing All Featured Locations</p>
              </div>
            )}
          </div>
        </div>

        <div className="wrapper">
          {(geolocating || searching) && <h1>LOADING</h1>}

          {isDynamic &&
            !noResults &&
            !searching &&
            locations?.length > 0 &&
            locationsShowing?.map((p: DynamicLocationFields) => {
              return (
                <LocationCard
                  location={p}
                  key={`location-${p.title}`}
                  coords={coords}
                />
              );
            })}

          {(noResults || !isDynamic) &&
            !searching &&
            locations?.length > 0 &&
            locationsShowing?.map((p: LocationItem) => {
              return (
                <ManualLocationCard
                  location={p}
                  key={`manual-location-${p.fields.LocationName?.value}`}
                />
              );
            })}
        </div>

        {locationsShowingCount < totalLocationsCount && (
          <button className="btn-tertiary load-btn" onClick={() => loadMore()}>
            View more
          </button>
        )}
      </div>
    </div>
  );
};

export default DynamicLocationContent;
